import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../state/auth.js';
import { translateError } from '../i18n.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { openFile } from '../utils/openFile.js';

// Onderhoud en keuringen (sportcomplex fase 3), for the complex's staff: items per place
// that are due every N months, what was done (by whom, with proof), and which are due
// soon or overdue. /onderhoud lists them, /onderhoud/:id opens one.

const ERRORS = {
  name_required: 'Geef het onderhoudspunt een naam.', invalid_name: 'De naam is te lang.', invalid_kind: 'Kies keuring of onderhoud.',
  invalid_interval: 'Kies hoe vaak: elke 1 tot 120 maanden.', invalid_date: 'Kies een geldige datum (niet in de toekomst bij uitvoeren).',
  invalid_location: 'Kies een plek van het complex.', item_stopped: 'Dit punt is stopgezet: zet het eerst weer aan.',
  before_last_done: 'Die datum ligt voor de laatste keer dat het gedaan is.', not_latest: 'Alleen de laatste uitvoering kan weg.',
  proof_type: 'Het bewijs moet een PDF of foto zijn (pdf, jpg, png of webp).', proof_too_large: 'Het bewijs is te groot (max. 10 MB).',
  invalid_performed_by: 'De naam van wie het deed is te lang.', invalid_note: 'De opmerking is te lang.', invalid_description: 'De omschrijving is te lang.',
};
const message = err => ERRORS[err.code] || translateError(err.code) || 'Dat lukte niet.';
const STATUS = {
  verlopen: { label: 'Verlopen', color: '#b91c1c', bg: '#fee2e2' },
  binnenkort: { label: 'Binnenkort', color: '#b45309', bg: '#fef3c7' },
  op_tijd: { label: 'Op tijd', color: '#047857', bg: '#d1fae5' },
  gestopt: { label: 'Gestopt', color: '#6b7280', bg: '#f3f4f6' },
};
const KIND = { keuring: 'Keuring', onderhoud: 'Onderhoud' };
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
// Dates are "YYYY-MM-DD" (club time): shown by their parts, never parsed (Safari)
const fmt = d => (d ? `${Number(d.slice(8, 10))} ${MONTHS[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}` : '—');
const todayLocal = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };
const every = n => (n === 12 ? 'elk jaar' : n === 1 ? 'elke maand' : n % 12 === 0 ? `elke ${n / 12} jaar` : `elke ${n} maanden`);
const muted = 'font-size:12px;color:var(--color-text-muted)';

function Pill({ status }) {
  const s = STATUS[status] || STATUS.op_tijd;
  return <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${s.bg};color:${s.color};white-space:nowrap`}>{s.label}</span>;
}

export default function MaintenanceView({ id }) {
  return id ? <ItemDetail key={id} id={id} /> : <ItemList />;
}

// Name, kind, place, how often, next date, description; for a new item or an edit
function ItemForm({ initial, locations, onSaved, onCancel }) {
  const [form, setForm] = useState(() => ({
    name: initial?.name || '', kind: initial?.kind || 'keuring', location_id: initial?.location_id ? String(initial.location_id) : '',
    interval_months: String(initial?.interval_months || 12), next_due: initial?.next_due || todayLocal(), description: initial?.description || '',
  }));
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  async function save() {
    setBusy(true); setError(null);
    const body = { ...form, location_id: form.location_id || null, interval_months: Number(form.interval_months) };
    try { onSaved(await api(initial ? `/api/maintenance/${initial.id}` : '/api/maintenance', { method: initial ? 'PUT' : 'POST', body: JSON.stringify(body) })); }
    catch (err) { setError(message(err)); }
    setBusy(false);
  }
  return (
    <div style="display:flex;flex-direction:column;gap:10px">
      <label class="form-label">Naam
        <input class="form-input" maxLength={150} value={form.name} onInput={e => set('name', e.target.value)} placeholder="bijv. AED kleedkamers, lichtmasten veld 1-4" />
      </label>
      <div style="display:flex;gap:10px;flex-wrap:wrap">
        <label class="form-label" style="flex:1 1 140px">Soort
          <select class="form-input" value={form.kind} onChange={e => set('kind', e.target.value)}>
            <option value="keuring">Keuring</option>
            <option value="onderhoud">Onderhoud</option>
          </select>
        </label>
        <label class="form-label" style="flex:1 1 180px">Plek
          <select class="form-input" value={form.location_id} onChange={e => set('location_id', e.target.value)}>
            <option value="">Hele complex</option>
            {locations.filter(l => l.is_active || String(l.id) === form.location_id).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
      </div>
      <div style="display:flex;gap:10px;flex-wrap:wrap">
        <label class="form-label" style="flex:1 1 140px">Elke … maanden
          <input class="form-input" type="number" min="1" max="120" value={form.interval_months} onInput={e => set('interval_months', e.target.value)} />
        </label>
        <label class="form-label" style="flex:1 1 180px">{initial ? 'Volgende datum' : 'Eerstvolgende datum'}
          <input class="form-input" type="date" value={form.next_due} onInput={e => set('next_due', e.target.value)} />
        </label>
      </div>
      <label class="form-label">Omschrijving (optioneel)
        <textarea class="form-input" rows={2} maxLength={2000} value={form.description} onInput={e => set('description', e.target.value)} placeholder="Wat moet er gebeuren, welke leverancier, normen…" />
      </label>
      {error && <div class="alert alert-error">{error}</div>}
      <div style="display:flex;gap:8px">
        <Button size="sm" disabled={busy || !form.name.trim()} onClick={save}>{busy ? 'Opslaan…' : 'Opslaan'}</Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>Annuleren</Button>
      </div>
    </div>
  );
}

function ItemList() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [status, setStatus] = useState('');
  const [location, setLocation] = useState('');
  const load = () => api('/api/maintenance').then(setData).catch(err => setError(message(err)));
  useEffect(() => { load(); }, []);
  if (error) return <div class="alert alert-error">{error}</div>;
  if (!data) return <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>;
  const count = s => data.items.filter(i => i.status === s).length;
  const shown = data.items.filter(i => (!status || i.status === status) && (!location || String(i.location_id || '') === location));
  return (
    <div>
      <div class="page-header" style="margin-bottom:16px">
        <div>
          <h1 class="page-title">Onderhoud & keuringen</h1>
          <div style={muted}>{count('verlopen')} verlopen · {count('binnenkort')} binnen 30 dagen · {count('op_tijd')} op tijd</div>
        </div>
        {!creating && <Button onClick={() => setCreating(true)}>+ Onderhoudspunt</Button>}
      </div>
      {creating && (
        <Card title="Nieuw onderhoudspunt" style="margin-bottom:16px">
          <ItemForm locations={data.locations} onCancel={() => setCreating(false)} onSaved={item => { setCreating(false); route(`/onderhoud/${item.id}`); }} />
        </Card>
      )}
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px">
        <select class="form-input" style="width:auto;min-width:150px" value={status} onChange={e => setStatus(e.target.value)} aria-label="Status">
          <option value="">Alle statussen</option>
          {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <select class="form-input" style="width:auto;min-width:150px" value={location} onChange={e => setLocation(e.target.value)} aria-label="Plek">
          <option value="">Alle plekken</option>
          {data.locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      <Card>
        {data.items.length === 0 && <div class="text-sm text-muted">Nog geen onderhoudspunten. Leg bijvoorbeeld de jaarlijkse keuring van de AED of de lichtmasten vast.</div>}
        {data.items.length > 0 && shown.length === 0 && <div class="text-sm text-muted">Niets met dit filter.</div>}
        {shown.map(i => (
          <a key={i.id} href={`/onderhoud/${i.id}`} onClick={e => { e.preventDefault(); route(`/onderhoud/${i.id}`); }}
            style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:10px 0;border-top:1px solid var(--color-border);color:inherit;text-decoration:none">
            <span style="flex:1 1 220px;min-width:0">
              <strong>{i.name}</strong>
              <span style={`${muted};display:block`}>{[KIND[i.kind], i.location_name || 'Hele complex', every(i.interval_months), i.last_done && `laatst ${fmt(i.last_done)}`].filter(Boolean).join(' · ')}</span>
            </span>
            <span style="font-size:13px;white-space:nowrap">{i.is_active ? fmt(i.next_due) : ''}</span>
            <Pill status={i.status} />
            <span aria-hidden="true" class="text-muted">›</span>
          </a>
        ))}
      </Card>
    </div>
  );
}

function ItemDetail({ id }) {
  const [item, setItem] = useState(null);
  const [locations, setLocations] = useState([]);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState({ done_on: todayLocal(), performed_by: '', note: '' });
  const [proof, setProof] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const load = () => Promise.all([api(`/api/maintenance/${id}`), api('/api/maintenance')])
    .then(([d, all]) => { setItem(d); setLocations(all.locations); setError(null); })
    .catch(err => setError(message(err)));
  useEffect(() => { load(); }, [id]);
  const back = <a href="/onderhoud" onClick={e => { e.preventDefault(); route('/onderhoud'); }} style="font-size:13px;color:var(--color-text-muted);text-decoration:none">‹ Onderhoud & keuringen</a>;
  if (!item) return <div>{back}{error ? <div class="alert alert-error" style="margin-top:12px">{error}</div> : <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>}</div>;

  async function record() {
    setBusy(true); setError(null);
    const fd = new FormData();
    for (const [k, v] of Object.entries(done)) fd.append(k, v);
    if (proof) fd.append('proof', proof);
    try {
      setItem(await api(`/api/maintenance/${id}/records`, { method: 'POST', body: fd, headers: {} }));
      setDone({ done_on: todayLocal(), performed_by: '', note: '' }); setProof(null); setInputKey(k => k + 1);
    } catch (err) { setError(message(err)); }
    setBusy(false);
  }
  async function removeLatest(r) {
    if (!confirm(`De uitvoering van ${fmt(r.done_on)} weghalen? De volgende datum gaat terug naar ${fmt(r.due_on)}.`)) return;
    setBusy(true); setError(null);
    try { setItem(await api(`/api/maintenance/${id}/records/${r.id}`, { method: 'DELETE' })); } catch (err) { setError(message(err)); }
    setBusy(false);
  }
  async function toggleActive() {
    if (item.is_active && !confirm(`"${item.name}" stopzetten? Er komen dan geen meldingen meer; de geschiedenis blijft.`)) return;
    setBusy(true); setError(null);
    try { setItem({ ...(await api(`/api/maintenance/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: !item.is_active }) })), records: item.records }); } catch (err) { setError(message(err)); }
    setBusy(false);
  }
  return (
    <div>
      {back}
      <div class="page-header" style="margin-top:6px;margin-bottom:12px">
        <div>
          <h1 class="page-title">{item.name}</h1>
          <div style={muted}>{[KIND[item.kind], item.location_name || 'Hele complex', every(item.interval_months)].join(' · ')}</div>
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          <Pill status={item.status} />
          {!editing && <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>Bewerken</Button>}
          <Button size="sm" variant="secondary" disabled={busy} onClick={toggleActive}>{item.is_active ? 'Stopzetten' : 'Weer aanzetten'}</Button>
        </div>
      </div>
      {error && <div class="alert alert-error mb-4">{error}</div>}

      {editing ? (
        <Card title="Bewerken" style="margin-bottom:16px">
          <ItemForm initial={item} locations={locations} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />
        </Card>
      ) : (
        <Card style="margin-bottom:16px">
          <div style="font-size:14px">{item.is_active ? <>Volgende keer: <strong>{fmt(item.next_due)}</strong></> : 'Stopgezet: er komen geen meldingen.'}</div>
          {item.description && <div style="font-size:13px;color:var(--color-text-muted);white-space:pre-wrap;margin-top:6px">{item.description}</div>}
        </Card>
      )}

      {item.is_active && (
        <Card title="Uitgevoerd vastleggen" style="margin-bottom:16px">
          <div style="display:flex;flex-direction:column;gap:10px">
            <div style="display:flex;gap:10px;flex-wrap:wrap">
              <label class="form-label" style="flex:1 1 150px">Datum
                <input class="form-input" type="date" max={todayLocal()} value={done.done_on} onInput={e => setDone(d => ({ ...d, done_on: e.target.value }))} />
              </label>
              <label class="form-label" style="flex:2 1 200px">Door (naam of bedrijf)
                <input class="form-input" maxLength={150} value={done.performed_by} onInput={e => setDone(d => ({ ...d, performed_by: e.target.value }))} placeholder="bijv. Henk of Firma Veilig BV" />
              </label>
            </div>
            <label class="form-label">Opmerking (optioneel)
              <textarea class="form-input" rows={2} maxLength={2000} value={done.note} onInput={e => setDone(d => ({ ...d, note: e.target.value }))} placeholder="bevindingen, vervangen onderdelen…" />
            </label>
            <label class="form-label">Bewijs (PDF of foto, optioneel)
              <input key={inputKey} class="form-input" type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={e => setProof(e.target.files?.[0] || null)} />
            </label>
            <div><Button size="sm" disabled={busy || !done.done_on} onClick={record}>{busy ? 'Opslaan…' : 'Vastleggen'}</Button> <span style={muted}>De volgende datum wordt dan {every(item.interval_months)} later.</span></div>
          </div>
        </Card>
      )}

      <Card title="Geschiedenis">
        {item.records.length === 0 && <div class="text-sm text-muted">Nog niet uitgevoerd.</div>}
        {item.records.map((r, i) => (
          <div key={r.id} style="display:flex;gap:8px;align-items:flex-start;flex-wrap:wrap;padding:8px 0;border-top:1px solid var(--color-border)">
            <div style="flex:1 1 220px;min-width:0">
              <div style="font-size:14px"><strong>{fmt(r.done_on)}</strong>{r.performed_by ? ` · ${r.performed_by}` : ''} {r.on_time ? '' : <span style="font-size:11px;color:#b91c1c">te laat (gepland {fmt(r.due_on)})</span>}</div>
              {r.note && <div style={`${muted};white-space:pre-wrap`}>{r.note}</div>}
              <div style={muted}>Vastgelegd door {r.recorded_by_name || 'onbekend'}</div>
            </div>
            {r.has_proof && <Button size="sm" variant="secondary" onClick={() => openFile(`/api/maintenance/${id}/records/${r.id}/proof`, r.proof_name)}>📎 Bewijs</Button>}
            {i === 0 && <Button size="sm" variant="secondary" disabled={busy} onClick={() => removeLatest(r)}>Weghalen</Button>}
          </div>
        ))}
      </Card>
    </div>
  );
}
